
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Guide, GuideFormData } from "@/types/guides";
import { useUpload } from "@/hooks/use-upload";
import { toast } from "sonner";

interface GuideFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: Omit<Guide, 'id' | 'created_at'>) => void;
  initialData?: Partial<Guide>;
  mode: 'create' | 'edit';
}

export function GuideForm({ open, onOpenChange, onSubmit, initialData, mode }: GuideFormProps) {
  // Format categories for the form - convert array to comma-separated string if needed
  const formattedInitialData = {
    ...initialData,
    categories: Array.isArray(initialData?.categories)
      ? initialData.categories.join(', ')
      : initialData?.categories || ''
  };
  
  const { register, handleSubmit, reset, setValue, watch } = useForm<GuideFormData>({
    defaultValues: formattedInitialData || {
      title: '',
      slug: '',
      description: '',
      categories: '',
      content: '',
      download_url: null,
      cover_image_url: '',
    }
  });

  // Reset the form when initialData changes or when the dialog opens/closes
  useEffect(() => {
    if (open && initialData) {
      // Format categories for the form
      const categoriesValue = Array.isArray(initialData.categories)
        ? initialData.categories.join(', ')
        : initialData.categories || '';
        
      reset({
        ...initialData,
        categories: categoriesValue
      });
    } else if (!open) {
      // Reset form when closing without initialData
      if (!initialData) {
        reset({
          title: '',
          slug: '',
          description: '',
          categories: '',
          content: '',
          download_url: null,
          cover_image_url: '',
        });
      }
    }
  }, [initialData, open, reset]);

  const { uploadImage } = useUpload();
  const coverImageUrl = watch('cover_image_url');

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const uploadResult = await uploadImage(file, 'public');
      if (uploadResult && uploadResult.url) {
        setValue('cover_image_url', uploadResult.url);
        toast.success('Cover image uploaded successfully');
      }
    } catch (error) {
      toast.error('Failed to upload cover image');
      console.error('Upload error:', error);
    }
  };

  const handleFormSubmit = handleSubmit((data) => {
    // Convert comma-separated string to array if needed
    const formattedData = {
      ...data,
      categories: typeof data.categories === 'string' 
        ? data.categories.split(',').map(c => c.trim()).filter(Boolean) 
        : (data.categories || [])
    };
    
    onSubmit(formattedData as Omit<Guide, 'id' | 'created_at'>);
    onOpenChange(false);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Create New Guide' : 'Edit Guide'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleFormSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Cover Image</label>
            <div className="space-y-2">
              {coverImageUrl && (
                <img
                  src={coverImageUrl}
                  alt="Cover preview"
                  className="w-full h-32 object-cover rounded-lg"
                />
              )}
              <Input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="cursor-pointer"
              />
            </div>
          </div>
          <div>
            <label htmlFor="title" className="text-sm font-medium">Title</label>
            <Input id="title" {...register('title')} required />
          </div>
          <div>
            <label htmlFor="slug" className="text-sm font-medium">Slug</label>
            <Input id="slug" {...register('slug')} required />
          </div>
          <div>
            <label htmlFor="description" className="text-sm font-medium">Description</label>
            <Textarea id="description" {...register('description')} required />
          </div>
          <div>
            <label htmlFor="categories" className="text-sm font-medium">Categories (comma-separated)</label>
            <Input 
              id="categories" 
              {...register('categories')} 
              placeholder="Fair Deal, Financial Support"
            />
          </div>
          <div>
            <label htmlFor="content" className="text-sm font-medium">Content</label>
            <Textarea id="content" {...register('content')} className="h-32" required />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit">
              {mode === 'create' ? 'Create Guide' : 'Update Guide'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
